import classNames from 'classnames/bind';
import React from 'react';

import styles from './TextInput.module.scss';
import { handleNumberKeyDown } from './TextInput.Functions';

/** Properties interface of the TextInput component. */
export interface TextInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  /** Aria Label for the TextInput component. */
  ariaLabel?: string;

  /** Class name for the TextInput component */
  className?: string;

  /** Determines if the TextInput component is disabled. */
  disabled?: boolean;

  /** Name of the TextInput component when submitted with a form. */
  name?: string;

  /**
   * Callback method for when the TextInput component text is changed.
   * @param e - A change event.
   */
  onChange?: React.ChangeEventHandler<HTMLInputElement>;

  /** Placeholder for the TextInput component */
  placeholder?: string;

  /** Determines if the TextInput component is read only. */
  readOnly?: boolean;

  /** Determines if the TextInput component is required. */
  required?: boolean;

  /** Focus order of the TextInput component. */
  tabIndex?: number;

  /**
   * Default behavior of the TextInput component.
   * @defaultValue `'text'`
   */
  type?: 'date' | 'email' | 'hidden' | 'number' | 'password' | 'tel' | 'text' | 'time' | 'url' | 'numeric';

  /** Value of the TextInput component when submitted with a form. */
  value?: string;
}

/** Default properties of the TextInput component. */
const defaultProps: Partial<TextInputProps> = {
  type: 'text',
};

/**
 * Classnames binding for the TextInput component.
 * @see [Classnames Usage](https://github.com/JedWatson/classnames#alternate-bind-version-for-css-modules)
 */
const cx = classNames.bind(styles);

/**
 * A Sitecore-ready implementation of the text input component.
 *
 * @see [Orchid Storybook](https://upmchporchid.azurewebsites.net/storybook/?path=/story/components-textinput--default) for design references
 * @see [Orchid Design System](https://upmchporchid.azurewebsites.net/components/textinput) for usage details
 *
 * @example
 * ```tsx
 * <TextInput />
 * ```
 *
 * @param props - TextInput properties.
 * @returns A JSX element.
 */
const TextInput: React.FC<TextInputProps> = (propsWithoutDefaults) => {
  const props = {
    type: propsWithoutDefaults.type ?? defaultProps.type,
    ...propsWithoutDefaults,
  };

  const {
    ariaLabel,
    className,
    disabled,
    onChange,
    placeholder,
    readOnly,
    required,
    tabIndex,
    type,
    maxLength,
    value,
    ...rest
  } = props;

  /** Combined className string for the underlying input element. */
  const classes: string = cx({ 'text-input': true }, className);

  /** Determines if the input element is interactive. */
  const interactive = !disabled && !readOnly;

  const handleKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (type === 'number' || type === 'numeric') {
      return handleNumberKeyDown(e);
    }

    return true;
  };

  /**
   * Event handler for when the input element is changed.
   * @param e - A change event.
   */
  const handleChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    if (!interactive || (maxLength && e.target.value?.length > maxLength)) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    if (onChange) {
      onChange(e);
    }
  };

  return (
    <input
      aria-label={ariaLabel}
      maxLength={maxLength}
      className={classes}
      disabled={disabled}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      placeholder={placeholder}
      readOnly={readOnly}
      required={required}
      tabIndex={disabled ? -1 : tabIndex}
      type={type}
      value={value}
      {...rest}
    />
  );
};

TextInput.displayName = 'TextInput';

export default TextInput;
